/**
 * Gardens Service - Irrigo Platform
 * Servicio para gestión de jardines, zonas, histórico y meteorología
 */

import { api, ApiResponse, PaginatedResponse, PaginationParams } from './api';
import type { 
  ApiGarden, 
  CreateGardenRequest, 
  UpdateGardenRequest,
  ApiZone,
  CreateZoneRequest,
  UpdateZoneRequest,
  ZoneAssignmentRequest,
  ApiWeatherData,
  ApiIrrigationEvent,
  ApiHistoryStats,
  ApiTimelineData,
  ApiWeatherHistory,
  ApiController,
  ApiSensor,
  ApiWeatherResponse,
  ApiWeatherCurrentResponse,
  ApiWeatherHourlyResponse,
  ApiCheckIrrigationResponse,
} from '@/types/api';

// ============================================================================
// TYPES
// ============================================================================

export interface GardenFilters extends PaginationParams {
  search?: string;
  companyId?: string;
  municipality?: string;
}

export interface HistoryFilters extends PaginationParams {
  start?: string;
  end?: string;
}

export interface WeatherFilters {
  municipality?: string;
  days?: number;
}

// ============================================================================
// GARDENS SERVICE
// ============================================================================

export const gardensService = {
  // ==========================================================================
  // GARDENS CRUD
  // ==========================================================================

  /**
   * Listar jardines
   */
  async getAll(params?: GardenFilters): Promise<PaginatedResponse<ApiGarden>> {
    return api.getPaginated<ApiGarden>('/gardens', params);
  },

  /**
   * Obtener jardín por ID
   */
  async getById(id: string): Promise<ApiResponse<ApiGarden>> {
    return api.get<ApiGarden>(`/gardens/${id}`);
  },

  /**
   * Crear nuevo jardín
   */
  async create(data: CreateGardenRequest): Promise<ApiResponse<ApiGarden>> {
    return api.post<ApiGarden>('/gardens', data);
  },

  /**
   * Actualizar jardín
   */
  async update(id: string, data: UpdateGardenRequest): Promise<ApiResponse<ApiGarden>> {
    return api.put<ApiGarden>(`/gardens/${id}`, data);
  },

  /**
   * Eliminar jardín
   */
  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/gardens/${id}`);
  },

  // ==========================================================================
  // GARDEN RESOURCES
  // ==========================================================================

  /**
   * Obtener controladores de un jardín
   */
  async getControllers(gardenId: string): Promise<ApiResponse<{ items: ApiController[] }>> {
    return api.get<{ items: ApiController[] }>(`/gardens/${gardenId}/controllers`);
  },

  /**
   * Obtener sensores de un jardín
   */
  async getSensors(gardenId: string): Promise<ApiResponse<{ items: ApiSensor[] }>> {
    return api.get<{ items: ApiSensor[] }>(`/gardens/${gardenId}/sensors`);
  },

  // ==========================================================================
  // ZONES
  // ==========================================================================

  /**
   * Listar zonas de un jardín
   * ACTUALIZADO: El backend ahora retorna formato paginado { items: [], pagination: {} }
   */
  async getZones(gardenId: string): Promise<PaginatedResponse<ApiZone>> {
    return api.getPaginated<ApiZone>(`/gardens/${gardenId}/zones`);
  },

  /**
   * Obtener zona por ID
   */
  async getZoneById(gardenId: string, zoneId: string): Promise<ApiResponse<ApiZone>> {
    return api.get<ApiZone>(`/gardens/${gardenId}/zones/${zoneId}`);
  },

  /**
   * Crear zona
   */
  async createZone(gardenId: string, data: CreateZoneRequest): Promise<ApiResponse<ApiZone>> {
    return api.post<ApiZone>(`/gardens/${gardenId}/zones`, data);
  },

  /**
   * Actualizar zona
   */
  async updateZone(gardenId: string, zoneId: string, data: UpdateZoneRequest): Promise<ApiResponse<ApiZone>> {
    return api.put<ApiZone>(`/gardens/${gardenId}/zones/${zoneId}`, data);
  },

  /**
   * Eliminar zona
   */
  async deleteZone(gardenId: string, zoneId: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/gardens/${gardenId}/zones/${zoneId}`);
  },

  /**
   * Asignar controlador/válvula a zona
   */
  async assignToZone(gardenId: string, zoneId: string, data: ZoneAssignmentRequest): Promise<ApiResponse<ApiZone>> {
    return api.post<ApiZone>(`/gardens/${gardenId}/zones/${zoneId}/assign`, data);
  },

  /**
   * Eliminar asignación de zona
   */
  async unassignFromZone(gardenId: string, zoneId: string, assignmentId: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/gardens/${gardenId}/zones/${zoneId}/assign/${assignmentId}`);
  },

  /**
   * Obtener timeline de una zona
   */
  async getZoneTimeline(gardenId: string, zoneId: string, date: string): Promise<ApiResponse<ApiTimelineData>> {
    return api.get<ApiTimelineData>(`/gardens/${gardenId}/zones/${zoneId}/timeline`, { date });
  },

  // ==========================================================================
  // WEATHER (AEMET)
  // El municipio se obtiene automáticamente del jardín
  // ==========================================================================

  /**
   * Obtener previsión meteorológica del jardín (7 días por defecto)
   * El municipio se obtiene automáticamente del jardín
   */
  async getWeather(gardenId: string, days?: number): Promise<ApiResponse<ApiWeatherResponse>> {
    const params = days ? { days } : undefined;
    return api.get<ApiWeatherResponse>(`/gardens/${gardenId}/weather`, params);
  },

  /**
   * Obtener datos meteorológicos actuales + próximas 6 horas
   */
  async getWeatherCurrent(gardenId: string): Promise<ApiResponse<ApiWeatherCurrentResponse>> {
    return api.get<ApiWeatherCurrentResponse>(`/gardens/${gardenId}/weather/current`);
  },

  /**
   * Obtener datos horarios del día actual (0-23h)
   */
  async getWeatherHourly(gardenId: string): Promise<ApiResponse<ApiWeatherHourlyResponse>> {
    return api.get<ApiWeatherHourlyResponse>(`/gardens/${gardenId}/weather/hourly`);
  },

  /**
   * Verificar si se debe regar basado en condiciones meteorológicas
   */
  async checkIrrigation(gardenId: string, durationMinutes?: number): Promise<ApiResponse<ApiCheckIrrigationResponse>> {
    return api.post<ApiCheckIrrigationResponse>(`/gardens/${gardenId}/weather/check-irrigation`, { durationMinutes });
  },

  // ==========================================================================
  // HISTORY
  // ==========================================================================

  /**
   * Obtener histórico de eventos de riego
   */
  async getHistory(gardenId: string, params?: HistoryFilters): Promise<PaginatedResponse<ApiIrrigationEvent>> {
    return api.getPaginated<ApiIrrigationEvent>(`/gardens/${gardenId}/history`, params);
  },

  /**
   * Obtener estadísticas de riego
   */
  async getHistoryStats(gardenId: string, params?: { start?: string; end?: string }): Promise<ApiResponse<ApiHistoryStats>> {
    return api.get<ApiHistoryStats>(`/gardens/${gardenId}/history/stats`, params);
  },

  /**
   * Obtener histórico meteorológico
   */
  async getHistoryWeather(gardenId: string, params?: HistoryFilters & { municipality?: string }): Promise<PaginatedResponse<ApiWeatherHistory>> {
    return api.getPaginated<ApiWeatherHistory>(`/gardens/${gardenId}/history/weather`, params);
  },
};

export default gardensService;
